import { useState } from 'react';
import {
  FileText,
  Globe,
  StickyNote,
  Quote,
  Upload,
  Pencil,
  Trash2,
  ChevronDown,
  ChevronRight,
  MapPin,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { CitationWithSource } from '@/hooks/useProjectCitations';
import { Chapter, Section } from '@/hooks/useChapters';
import { cn } from '@/lib/utils';

const typeIcons: Record<string, React.ElementType> = {
  note: StickyNote,
  upload: Upload,
  web_snapshot: Globe,
  quote: Quote,
};

const typeLabels: Record<string, string> = {
  note: 'Note',
  upload: 'Upload',
  web_snapshot: 'Web',
  quote: 'Quote',
};

interface CitationListItemProps {
  citation: CitationWithSource;
  chapters: Chapter[];
  sections: Record<string, Section[]>;
  onEdit: () => void;
  onDelete: () => void;
}

export function CitationListItem({
  citation,
  chapters,
  sections,
  onEdit,
  onDelete,
}: CitationListItemProps) {
  const [expanded, setExpanded] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  
  const { research_item } = citation;
  const TypeIcon = typeIcons[research_item.type] || FileText;
  
  // Find section and chapter names
  let sectionName = 'Unassigned';
  let chapterName = '';
  
  if (citation.section_id) {
    for (const chapter of chapters) {
      const chapterSections = sections[chapter.id] || [];
      const section = chapterSections.find(s => s.id === citation.section_id);
      if (section) {
        sectionName = section.title;
        chapterName = chapter.title;
        break;
      }
    }
  }
  
  const hasDetails = citation.citation_text || citation.page_reference;

  return (
    <>
      <div className="border rounded-lg bg-card overflow-hidden">
        {/* Header */}
        <div 
          className={cn(
            "p-3 flex items-start gap-3 cursor-pointer hover:bg-muted/50 transition-colors",
            expanded && "border-b"
          )}
          onClick={() => hasDetails && setExpanded(!expanded)}
        >
          <div className={cn(
            "h-8 w-8 rounded-md flex items-center justify-center shrink-0",
            "bg-primary/10 text-primary"
          )}>
            <TypeIcon className="h-4 w-4" />
          </div>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <p className="font-medium text-sm truncate">
                {research_item.source_author || research_item.title}
              </p>
              {citation.is_direct_quote && (
                <Badge variant="secondary" className="text-[10px] px-1.5 shrink-0">
                  Quote
                </Badge>
              )}
            </div>
            
            {research_item.source_author && (
              <p className="text-xs text-muted-foreground truncate">
                {research_item.title}
              </p>
            )}
            
            <div className="flex items-center gap-1.5 mt-1.5 text-xs text-muted-foreground">
              <MapPin className="h-3 w-3" />
              <span className="truncate">
                {citation.section_id ? (
                  <>
                    {chapterName && <span>{chapterName} &rsaquo; </span>}
                    {sectionName}
                  </>
                ) : (
                  <span className="text-amber-600">Unassigned</span>
                )}
              </span>
              {citation.page_reference && (
                <>
                  <span className="text-muted-foreground/50">•</span>
                  <span>p. {citation.page_reference}</span>
                </>
              )}
            </div>
          </div>
          
          <div className="flex items-center gap-1 shrink-0">
            {hasDetails && (
              <div className="h-6 w-6 flex items-center justify-center text-muted-foreground">
                {expanded ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                )}
              </div>
            )}
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={(e) => {
                e.stopPropagation();
                onEdit();
              }}
            >
              <Pencil className="h-3.5 w-3.5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-destructive hover:text-destructive"
              onClick={(e) => {
                e.stopPropagation();
                setShowDeleteConfirm(true);
              }}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
        
        {/* Expanded Details */}
        {expanded && hasDetails && (
          <div className="p-3 bg-muted/30 text-sm space-y-2">
            {citation.citation_text && (
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1">Citation Text</p>
                <p className={cn(
                  "text-sm",
                  citation.is_direct_quote && "italic"
                )}>
                  {citation.is_direct_quote && '"'}
                  {citation.citation_text}
                  {citation.is_direct_quote && '"'}
                </p>
              </div>
            )}
            {citation.page_reference && (
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1">Page Reference</p>
                <p className="text-sm">{citation.page_reference}</p>
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Citation</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove this citation? This won't delete the research item itself.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={onDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
